import React, { useState, useEffect } from 'react';

import { Bell, X, Check, Sparkles } from 'lucide-react';
import { subscribeToPushNotifications, getPushSubscriptionStatus } from '../utils/pushNotifications';
import { IS_NATIVE } from '../utils/http';
import { safeLocalStorage } from '../utils/safeUtils';

const NotificationPermissionPrompt = () => {
  const [show, setShow] = useState(false);
  const [loading, setLoading] = useState(false);
  const [closed, setClosed] = useState(false);

  useEffect(() => {
    // Sur native, les notifications sont gérées par nativePushNotifications.js
    // Ce composant est uniquement pour le web PWA
    if (IS_NATIVE) {
      return;
    }
    
    // Vérifier le statut des notifications après 10 secondes pour laisser le temps au service worker de se charger
    const timer = setTimeout(async () => {
      try {
        const status = await getPushSubscriptionStatus();
        
        // Vérifier si l'utilisateur a déjà fermé le prompt dans cette session
        const hasClosedPrompt = safeLocalStorage.getItem('notification-prompt-closed');
        
        // Afficher le prompt si:
        // 1. Les notifications sont supportées
        // 2. L'utilisateur n'est pas déjà souscrit
        // 3. La permission n'est pas déjà refusée
        // 4. L'utilisateur n'a pas fermé le prompt dans cette session
        if (
          status.supported && 
          !status.subscribed && 
          status.permission !== 'denied' &&
          !hasClosedPrompt
        ) {
          setShow(true);
        }
      } catch (error) {
        console.warn('Erreur vérification statut notifications:', error);
      }
    }, 10000);

    return () => clearTimeout(timer);
  }, []);

  const handleAccept = async () => {
    setLoading(true);
    try {
      const subscription = await subscribeToPushNotifications(window.location.origin);
      if (subscription) {
        // Succès - fermer le prompt après animation
        setTimeout(() => {
          setShow(false);
        }, 2000);
      } else {
        // Si échec, on ferme quand même le prompt
        setShow(false);
      }
    } catch (error) {
      // Gérer silencieusement les erreurs de souscription push
      // pour ne pas bloquer l'expérience utilisateur
      console.warn('⚠️ Erreur souscription push notifications:', error.message);
      setShow(false);
    } finally {
      setLoading(false);
    }
  };

  const handleDecline = () => {
    setClosed(true);
    setTimeout(() => {
      setShow(false);
      // Enregistrer que l'utilisateur a fermé le prompt
      safeLocalStorage.setItem('notification-prompt-closed', 'true');
    }, 300);
  };

  const handleRemindLater = () => {
    setClosed(true);
    setTimeout(() => {
      setShow(false);
    }, 300);
  };

  if (!show) return null;

  return (
    <>
      {/* Overlay semi-transparent */}
      <div 
        className={`fixed inset-0 bg-black bg-opacity-50 z-40 transition-opacity duration-300 ${
          closed ? 'opacity-0' : 'opacity-100'
        }`}
        onClick={handleDecline}
      />

      {/* Modal de notification */}
      <div 
        className={`fixed top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 z-50 w-11/12 max-w-md transition-all duration-300 ${
          closed ? 'scale-95 opacity-0' : 'scale-100 opacity-100'
        }`}
      >
        <div className="bg-white rounded-2xl shadow-2xl overflow-hidden">
          {/* Header avec dégradé */}
          <div className="bg-gradient-to-br from-blue-500 via-purple-500 to-pink-500 p-6 relative">
            <button
              onClick={handleDecline}
              className="absolute top-3 right-3 p-1 text-white hover:bg-white hover:bg-opacity-20 rounded-full transition-colors"
            >
              <X className="w-5 h-5" />
            </button>

            <div className="flex items-center justify-center mb-3">
              <div className="relative">
                <div className="absolute inset-0 animate-ping bg-white rounded-full opacity-20"></div>
                <div className="relative bg-white rounded-full p-3">
                  <Bell className="w-8 h-8 text-blue-500" />
                </div>
              </div>
            </div>

            <h2 className="text-2xl font-bold text-white text-center mb-2">
              Activez les notifications
            </h2>
            <p className="text-white text-opacity-90 text-center text-sm">
              Ne manquez plus aucune récompense ! 🎉
            </p>
          </div>

          {/* Contenu */}
          <div className="p-6">
            <div className="space-y-4 mb-6">
              {/* Avantages */}
              <div className="flex items-start space-x-3">
                <div className="flex-shrink-0 mt-1">
                  <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center">
                    <Sparkles className="w-4 h-4 text-blue-600" />
                  </div>
                </div>
                <div>
                  <h3 className="font-semibold text-gray-800 text-sm">Badges débloqués</h3>
                  <p className="text-gray-600 text-xs">Soyez notifié instantanément quand vous gagnez un badge</p>
                </div>
              </div>

              <div className="flex items-start space-x-3">
                <div className="flex-shrink-0 mt-1">
                  <div className="w-8 h-8 bg-purple-100 rounded-full flex items-center justify-center">
                    <Check className="w-4 h-4 text-purple-600" />
                  </div>
                </div>
                <div>
                  <h3 className="font-semibold text-gray-800 text-sm">Lavages approuvés</h3>
                  <p className="text-gray-600 text-xs">Recevez une confirmation dès validation de votre lavage</p>
                </div>
              </div>

              <div className="flex items-start space-x-3">
                <div className="flex-shrink-0 mt-1">
                  <div className="w-8 h-8 bg-pink-100 rounded-full flex items-center justify-center">
                    <Bell className="w-4 h-4 text-pink-600" />
                  </div>
                </div>
                <div>
                  <h3 className="font-semibold text-gray-800 text-sm">Classements & streaks</h3>
                  <p className="text-gray-600 text-xs">Restez informé de votre progression et de vos séries</p>
                </div>
              </div>
            </div>

            {/* Boutons d'action */}
            <div className="space-y-3">
              <button
                onClick={handleAccept}
                disabled={loading}
                className="w-full bg-gradient-to-r from-blue-500 to-purple-600 text-white font-semibold py-3 px-6 rounded-xl hover:from-blue-600 hover:to-purple-700 transition-all shadow-lg hover:shadow-xl disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center space-x-2"
              >
                {loading ? (
                  <>
                    <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                    <span>Activation...</span>
                  </>
                ) : (
                  <>
                    <Bell className="w-5 h-5" />
                    <span>Activer les notifications</span>
                  </>
                )}
              </button>

              <button
                onClick={handleRemindLater}
                className="w-full bg-gray-100 text-gray-700 font-medium py-2.5 px-6 rounded-xl hover:bg-gray-200 transition-colors text-sm"
              >
                Me rappeler plus tard
              </button>

              <button
                onClick={handleDecline}
                className="w-full text-gray-500 hover:text-gray-700 font-medium py-2 text-sm transition-colors"
              >
                Non merci
              </button>
            </div>

            {/* Note de confidentialité */}
            <p className="text-xs text-gray-400 text-center mt-4">
              🔒 Vous pouvez désactiver ces notifications à tout moment dans les paramètres
            </p>
          </div>
        </div>
      </div>
    </>
  );
};

export default NotificationPermissionPrompt;
